import React from 'react';
import { motion } from 'framer-motion';
import { Play, Star, Map, Palette, Hexagon, GraduationCap, Sparkles, Award, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { calculateStudentXP, getStudentRank } from '../utils/gamification';

const Card = ({ title, icon: Icon, color, to, delay, badge }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay, duration: 0.3 }}
    >
        <Link to={to} className={`block h-full p-6 rounded-3xl ${color} shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all transform cursor-pointer text-white relative overflow-hidden group`}>
            {badge && (
                <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider text-white">
                    {badge}
                </div>
            )}
            <div className="absolute top-0 right-0 p-4 opacity-20 transform group-hover:scale-125 transition-transform duration-500">
                <Icon size={120} />
            </div>
            <div className="relative z-10">
                <div className="bg-white/20 w-16 h-16 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm">
                    <Icon size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-2">{title}</h3>
                <div className="flex items-center space-x-2 text-sm font-medium opacity-90">
                    <span>Entrar</span>
                    <Play size={16} fill="currentColor" />
                </div>
            </div>
        </Link>
    </motion.div>
);

const Home = () => {
    const { user, progress } = useAuth();
    const isTeacher = user?.role === 'teacher';

    // Gamificación: cálculo de XP y Rango del estudiante
    const userProgress = (user && progress && progress[user.name]) || {};
    const studentXp = calculateStudentXP(userProgress);
    const rank = getStudentRank(studentXp);

    return (
        <div className="space-y-8 pb-12">
            <div className="text-center space-y-3">
                <motion.h1
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-4xl md:text-5xl font-extrabold text-brand-purple"
                >
                    ¡Hola, {user?.name || 'Explorador'}! 👋
                </motion.h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    {isTeacher
                        ? 'Bienvenido al entorno educativo de GeoCeuta. Gestiona tu clase y revisa el aprendizaje de tus alumnos.'
                        : 'Bienvenido a tu aventura de geometría. ¡Supera desafíos y sube de nivel!'}
                </p>
            </div>

            {/* Hero Banner de Rango y Experiencia (XP) para Alumnos */}
            {!isTeacher && (
                <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                    className="max-w-4xl mx-auto bg-white/90 backdrop-blur-xl rounded-3xl p-6 shadow-xl border-2 border-brand-purple/20 relative overflow-hidden"
                >
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
                        {/* Insignia y Rango */}
                        <div className="flex items-center gap-4 text-center md:text-left">
                            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${rank.gradient} flex items-center justify-center text-4xl shadow-md transform -rotate-3 hover:rotate-0 transition-transform`}>
                                {rank.icon}
                            </div>
                            <div>
                                <div className="flex items-center gap-2 justify-center md:justify-start">
                                    <span className={`text-xs font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full text-white ${rank.badgeColor}`}>
                                        Nivel {rank.level} de 5
                                    </span>
                                    <span className="text-xs font-bold text-gray-400">Rango Actual</span>
                                </div>
                                <h2 className="text-2xl font-black text-gray-800 mt-0.5">{rank.title}</h2>
                                <p className="text-xs text-gray-500 max-w-sm hidden sm:block">{rank.description}</p>
                            </div>
                        </div>

                        {/* Barra de Progreso XP y Estado */}
                        <div className="flex-1 w-full md:max-w-xs flex flex-col gap-2">
                            <div className="flex items-center justify-between text-xs font-bold">
                                <span className="text-gray-600 flex items-center gap-1">
                                    <Sparkles size={14} className="text-amber-500" />
                                    <span>Puntos de Experiencia:</span>
                                </span>
                                <span className="text-brand-purple font-black text-sm">
                                    {rank.totalXp} <span className="text-gray-400 text-xs font-medium">/ {rank.isMaxLevel ? '1.000' : rank.nextRank.minXp} XP</span>
                                </span>
                            </div>

                            {/* Barra con gradiente */}
                            <div className="w-full bg-gray-100 rounded-full h-3.5 p-0.5 overflow-hidden shadow-inner border border-gray-200">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${rank.progressPercent}%` }}
                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                    className={`h-full rounded-full bg-gradient-to-r ${rank.gradient} shadow-sm`}
                                />
                            </div>

                            {/* Mensaje motivacional */}
                            <div className="text-[11px] text-gray-500 text-center md:text-right font-medium">
                                {rank.isMaxLevel ? (
                                    <span className="text-emerald-600 font-bold">🏆 ¡Máxima maestría alcanzada! Eres un genio espacial.</span>
                                ) : (
                                    <span>
                                        Faltan <strong className="text-brand-purple font-bold">{rank.xpRemaining} XP</strong> para ser <strong>{rank.nextRank.title} {rank.nextRank.icon}</strong>
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Enlace rápido a Logros */}
                        <Link
                            to="/achievements"
                            className="shrink-0 px-4 py-3 rounded-2xl bg-brand-yellow/10 hover:bg-brand-yellow/20 border-2 border-brand-yellow/30 text-amber-800 font-bold text-xs flex items-center gap-2 transition-all hover:scale-105 active:scale-95 shadow-sm"
                        >
                            <Award size={18} className="text-amber-600" />
                            <span>Ver Sala de Trofeos</span>
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {isTeacher && (
                    <Card
                        title="Panel Docente"
                        icon={GraduationCap}
                        color="bg-brand-orange"
                        to="/teacher"
                        delay={0.05}
                        badge="Profesorado"
                    />
                )}
                <Card
                    title="Geometría Mágica"
                    icon={Map}
                    color="bg-brand-blue"
                    to="/units"
                    delay={0.1}
                />
                <Card
                    title="Pizarra Creativa"
                    icon={Palette}
                    color="bg-brand-pink"
                    to="/whiteboard"
                    delay={0.15}
                />
                <Card
                    title="Geoplano Virtual"
                    icon={Hexagon}
                    color="bg-violet-600"
                    to="/geoboard"
                    delay={0.2}
                />
                <Card
                    title="Simetría y Espejo"
                    icon={Sparkles}
                    color="bg-teal-600"
                    to="/units/1/symmetry"
                    delay={0.25}
                />
                <Card
                    title="Sala de Logros"
                    icon={Star}
                    color="bg-brand-yellow"
                    to="/achievements"
                    delay={0.3}
                />
            </div>
        </div>
    );
};

export default Home;

